import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCoins, getPairs } from "@/lib/api";
import Hero from "@/components/Hero";
import CoinCard from "@/components/CoinCard";
import { ScrollReveal } from "@/hooks/useAnimations";
import { Flame } from "lucide-react";

const FILTERS = [
  { key: "All", testid: "filter-all-button" },
  { key: "Large Cap", testid: "filter-largecap-button" },
  { key: "Stocks & Gold", testid: "filter-stocks-button" },
  { key: "Memecoins", testid: "filter-memecoins-button" },
  { key: "Base Ecosystem", testid: "filter-ecosystem-button" },
];
const SORTS = [
  { key: "market_cap", label: "Market cap" },
  { key: "newest", label: "Newest" },
  { key: "volume", label: "Volume" },
];

export default function Home() {
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("market_cap");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const h = (e) => setSearch(e.detail);
    window.addEventListener("bl-search", h);
    return () => window.removeEventListener("bl-search", h);
  }, []);

  const { data: pairs = [] } = useQuery({ queryKey: ["pairs"], queryFn: getPairs });
  const pairMap = Object.fromEntries(pairs.map((p) => [p.symbol, p]));
  const { data: coins = [], isLoading } = useQuery({
    queryKey: ["coins", category, sort, search],
    queryFn: () => getCoins({ category, sort, search: search || undefined }),
  });

  return (
    <div>
      <Hero />

      <section id="coins" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="mb-6 flex items-center gap-2">
          <Flame size={22} className="text-[#ffb800]" />
          <h2 className="font-head text-2xl font-bold tracking-tight sm:text-3xl">Fresh coins</h2>
          <span className="rounded-full bg-white/5 px-2.5 py-1 font-mono2 text-xs text-slate-400">
            {coins.length}
          </span>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={80}>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={f.testid}
                onClick={() => setCategory(f.key)}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 active:scale-[0.97] ${
                  category === f.key
                    ? "border-blue-500 bg-blue-500/15 text-[#7fb0ff] shadow-[0_0_15px_rgba(0,82,255,0.25)]"
                    : "border-white/10 bg-[#131824] text-slate-400 hover:scale-[1.03] hover:border-white/25 hover:text-white"
                }`}
              >
                {f.key}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-white/10 bg-[#131824] p-1">
            {SORTS.map((s) => (
              <button
                key={s.key}
                data-testid={`sort-${s.key}`}
                onClick={() => setSort(s.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  sort === s.key ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        </ScrollReveal>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="coins-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-[#131824]/90 p-5">
                <div className="flex items-center gap-3">
                  <div className="skeleton-shimmer h-12 w-12 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton-shimmer h-3.5 w-2/3 rounded" />
                    <div className="skeleton-shimmer h-3 w-1/2 rounded" />
                  </div>
                </div>
                <div className="skeleton-shimmer mt-4 h-6 w-3/4 rounded-full" />
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/5 pt-3">
                  <div className="skeleton-shimmer h-8 rounded" />
                  <div className="skeleton-shimmer h-8 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : coins.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 py-24 text-center text-slate-500">
            No coins yet — be the first to launch one.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {coins.map((c, i) => (
              <ScrollReveal key={c.id} delay={(i % 4) * 60}>
                <CoinCard coin={c} pair={pairMap[c.pair_symbol]} />
              </ScrollReveal>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-slate-600">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-2 px-4">
          <div className="flex items-center gap-4">
            <a href="https://x.com/Bluepadfamily" target="_blank" rel="noreferrer" className="text-slate-400 transition-colors hover:text-white" data-testid="footer-x-link">@Bluepadfamily</a>
            <span className="text-slate-700">·</span>
            <a href="https://bluepad.family" target="_blank" rel="noreferrer" className="text-slate-400 transition-colors hover:text-white">bluepad.family</a>
          </div>
          <div>BluePad · Uniswap v4 launchpad on Base · Not financial advice</div>
        </div>
      </footer>
    </div>
  );
}
